<template>
  <div>
    <transition name="call-qv-fade">
      <div
        v-if="open"
        class="call-qv__backdrop"
        @click="$emit('close')"
      />
    </transition>
    <transition name="call-qv-slide">
      <aside
        v-if="open"
        class="call-qv"
        role="dialog"
        aria-modal="true"
        aria-label="Quick calls"
      >
        <header class="call-qv__header">
          <div class="call-qv__title-block">
            <span class="call-qv__accent" aria-hidden="true"></span>
            <div>
              <h2 class="call-qv__title">Call Logs</h2>
              <p class="call-qv__subtitle">View Call History</p>
            </div>
          </div>
          <div class="call-qv__header-actions">
            <button
              type="button"
              class="call-qv__icon-btn"
              :class="{ 'is-loading': loadingCalls }"
              :disabled="loadingCalls"
              :title="loadingCalls ? 'Refreshing…' : 'Refresh recent calls'"
              aria-label="Refresh recent calls"
              @click="fetchTodayCalls(true)"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
                <path d="M20.49 15A9 9 0 0 1 5.64 18.36L1 14"/>
              </svg>
            </button>
            <button
              type="button"
              class="call-qv__icon-btn"
              title="Close"
              aria-label="Close quick calls"
              @click="$emit('close')"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </header>

        <div class="call-qv__body">
          <!-- Today's calls -->
          <section class="call-qv__section">
            <header class="call-qv__section-head">
              <h3>Today's Calls</h3>
              <span class="call-qv__badge-count">{{ todayCalls.length }}</span>
            </header>

            <div v-if="loadingCalls && !hasLoadedCalls" class="call-qv__skeletons">
              <div v-for="n in 4" :key="n" class="call-qv__skeleton"/>
            </div>

            <div v-else-if="callsError" class="call-qv__state call-qv__state--error">
              <p>Couldn't load today's calls.</p>
              <button type="button" class="call-qv__retry" @click="fetchTodayCalls(true)">Retry</button>
            </div>

            <ul v-else-if="todayCalls.length" class="call-qv__list">
              <li
                v-for="(call, index) in todayCalls"
                :key="`${call.phone}-${call.date_time}-${index}`"
                class="call-qv__row"
                :class="{ 'call-qv__row--no-phone': !call.phone }"
                :title="call.phone ? `Call back ${call.name || call.phone}` : ''"
                @click="!isMobile && call.phone && callBack(call)"
              >
                <span class="call-qv__avatar" :style="avatarStyle(call.name || call.phone)">
                  {{ initials(call.name || call.phone) }}
                </span>
                <div class="call-qv__row-main">
                  <div class="call-qv__row-line">
                    <span class="call-qv__row-name">{{ call.name || 'Unknown' }}</span>
                    <span class="call-qv__row-time" :title="call.date_time || ''">{{ formatCallWhen(call) }}</span>
                  </div>
                  <div class="call-qv__row-line call-qv__row-line--meta">
                    <span class="call-qv__row-phone">{{ formatPhone(call.phone) }}</span>
                    <span
                      v-if="call.status"
                      class="call-qv__row-status"
                      :class="statusClass(call.status)"
                    >{{ call.status }}</span>
                  </div>
                </div>
                <a
                  v-if="isMobile && call.phone"
                  :href="`tel:${normalizedPhone(call.phone)}`"
                  class="call-qv__row-action"
                  :aria-label="`Call ${call.name || call.phone}`"
                  @click.stop
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </a>
                <button
                  v-else
                  type="button"
                  class="call-qv__row-action"
                  :disabled="!call.phone"
                  :aria-label="call.phone ? `Call back ${call.name || call.phone}` : 'No phone number'"
                  @click.stop="call.phone && callBack(call)"
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </button>
              </li>
            </ul>

            <div v-else class="call-qv__state">
              <p>No calls placed today yet.</p>
              <span class="call-qv__state-hint">Open the queue to start calling.</span>
            </div>
          </section>
        </div>

        <footer class="call-qv__footer">
          <button type="button" class="call-qv__footer-btn" @click="goToQueue">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polygon points="12 2 2 7 12 12 22 7 12 2"/>
              <polyline points="2 17 12 22 22 17"/>
              <polyline points="2 12 12 17 22 12"/>
            </svg>
            <span>Open Call Queue</span>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="call-qv__footer-arrow" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </button>
        </footer>
      </aside>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'

function isoToday () {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function readAuthUser () {
  try {
    const raw = localStorage.getItem('auth_user')
    return raw ? JSON.parse(raw) : null
  } catch (error) {
    return null
  }
}

// Matches the same mobile breakpoint used on the full Call Queue page so
// "mobile" means the same thing in both places.
const MOBILE_MEDIA_QUERY = '(max-width: 767px)'

export default {
  name: 'TheHeaderCallQuickView',
  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      dialNumber: '',
      dialTouched: false,
      todayCalls: [],
      loadingCalls: false,
      hasLoadedCalls: false,
      callsError: null,
      previousBodyOverflow: '',
      canPasteClipboard: false,
      isMobile: false,
      _mobileMql: null,
      _mobileListener: null,
    }
  },
  computed: {
    normalizedDial () {
      return String(this.dialNumber || '').replace(/\D/g, '').slice(-10)
    },
    isValidDial () {
      return this.normalizedDial.length === 10
    },
    dialError () {
      if (!this.dialTouched) return ''
      if (!this.dialNumber) return ''
      return this.isValidDial ? '' : 'Number must be 10 digits.'
    },
  },
  watch: {
    open (isOpen) {
      if (isOpen) {
        this.previousBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        this.dialTouched = false
        this.refreshClipboardSupport()
        if (!this.hasLoadedCalls && !this.loadingCalls) {
          this.fetchTodayCalls(false)
        }
        this.$nextTick(() => {
          if (this.$refs.dialInput) this.$refs.dialInput.focus()
        })
      } else {
        document.body.style.overflow = this.previousBodyOverflow || ''
      }
    },
  },
  mounted () {
    document.addEventListener('keydown', this.onKeydown)
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const mql = window.matchMedia(MOBILE_MEDIA_QUERY)
      this.isMobile = mql.matches
      const listener = (event) => { this.isMobile = event.matches }
      mql.addEventListener('change', listener)
      this._mobileMql = mql
      this._mobileListener = listener
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = this.previousBodyOverflow || ''
    if (this._mobileMql && this._mobileListener) {
      this._mobileMql.removeEventListener('change', this._mobileListener)
    }
    this._mobileMql = null
    this._mobileListener = null
  },
  methods: {
    onKeydown (event) {
      if (event.key === 'Escape' && this.open) {
        this.$emit('close')
      }
    },
    refreshClipboardSupport () {
      this.canPasteClipboard = !!(navigator.clipboard && typeof navigator.clipboard.readText === 'function' && window.isSecureContext)
    },
    onDialInput (event) {
      this.dialTouched = true
      const raw = event.target.value
      const cleaned = raw.replace(/[^\d+\s-]/g, '')
      if (cleaned !== raw) {
        this.dialNumber = cleaned
      }
    },
    clearDial () {
      this.dialNumber = ''
      this.dialTouched = false
      this.$nextTick(() => {
        if (this.$refs.dialInput) this.$refs.dialInput.focus()
      })
    },
    async pasteFromClipboard () {
      if (!this.canPasteClipboard) return
      try {
        const text = await navigator.clipboard.readText()
        if (text) {
          this.dialNumber = String(text).replace(/[^\d+\s-]/g, '').slice(0, 14)
          this.dialTouched = true
          this.$nextTick(() => {
            if (this.$refs.dialInput) this.$refs.dialInput.focus()
          })
        }
      } catch (error) {
        this.$toast.error('Couldn\'t read clipboard. Paste manually with Ctrl+V.')
      }
    },
    placeCall () {
      this.dialTouched = true
      if (!this.isValidDial) {
        return
      }
      const phone = this.normalizedDial
      this.$emit('close')
      this.$router.push({ path: '/telesales', query: { phone } }).catch(() => {})
    },
    callBack (call) {
      const phone = String(call && call.phone || '').replace(/\D/g, '').slice(-10)
      if (phone.length !== 10) return
      this.$emit('close')
      this.$router.push({
        path: '/telesales',
        query: {
          phone,
          ...(call.name ? { name: call.name } : {}),
        },
      }).catch(() => {})
    },
    goToQueue () {
      this.$emit('close')
      if (this.$route.path !== '/callqueue') {
        this.$router.push({ path: '/callqueue' }).catch(() => {})
      }
    },
    async fetchTodayCalls (manual) {
      const user = readAuthUser()
      const userId = user && user.id
      if (!userId) {
        this.callsError = new Error('No user id')
        return
      }

      this.loadingCalls = true
      this.callsError = null

      const today = isoToday()

      try {
        const response = await axios.post(
          `${this.$apiAdress}/api/calls/user_log?token=${localStorage.getItem('api_token')}`,
          { fromDate: today, toDate: today, user: String(userId) }
        )

        const list = Array.isArray(response.data) ? response.data : []
        this.todayCalls = list.slice().reverse()
        this.hasLoadedCalls = true
        if (manual) {
          this.$toast.success('Recent calls refreshed')
        }
      } catch (error) {
        this.callsError = error
        if (manual) {
          this.$toast.error('Couldn\'t refresh recent calls')
        }
      } finally {
        this.loadingCalls = false
      }
    },
    initials (text) {
      const parts = String(text || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },
    avatarStyle (text) {
      const palette = [
        ['#0e5b46', '#16a07a'],
        ['#1e3a8a', '#3b82f6'],
        ['#6d28d9', '#a855f7'],
        ['#b45309', '#f59e0b'],
        ['#be185d', '#ec4899'],
        ['#115e59', '#14b8a6'],
      ]
      const seed = String(text || '?')
      let hash = 0
      for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      const [from, to] = palette[hash % palette.length]
      return { background: `linear-gradient(135deg, ${from}, ${to})` }
    },
    formatPhone (raw) {
      const digits = String(raw || '').replace(/\D/g, '').slice(-10)
      if (digits.length !== 10) return raw || ''
      return `${digits.slice(0, 5)} ${digits.slice(5)}`
    },
    normalizedPhone (raw) {
      const digits = String(raw || '').replace(/\D/g, '').slice(-10)
      return digits.length === 10 ? digits : ''
    },
    // Prefer the raw Unix timestamp from the backend — it's the only
    // unambiguous form. The "date_time" string is IST-formatted by the server
    // and only used as a display fallback if `timestamp` is missing (e.g.,
    // older responses cached client-side). Building a Date directly from the
    // Unix int lets the browser format in the user's locale with no manual
    // timezone math.
    parseCallDateTime (call) {
      if (!call) return null

      if (typeof call.timestamp === 'number' && call.timestamp > 0) {
        const fromUnix = new Date(call.timestamp * 1000)
        if (!Number.isNaN(fromUnix.getTime())) return fromUnix
      }

      const raw = call.date_time
      if (!raw) return null
      const match = String(raw).match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
      if (!match) return null
      const [, dd, mm, yyyy, hh, mi, ss] = match
      const date = new Date(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(hh),
        Number(mi),
        Number(ss || 0)
      )
      return Number.isNaN(date.getTime()) ? null : date
    },
    formatCallWhen (call) {
      const date = this.parseCallDateTime(call)
      if (!date) return (call && call.date_time) ? String(call.date_time) : ''

      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffSec = Math.round(diffMs / 1000)
      const diffMin = Math.round(diffSec / 60)
      const diffHr = Math.round(diffMin / 60)

      // Fresh activity gets relative-time so tele staff see at-a-glance
      // recency. "Just now" up to a minute, then minutes/hours.
      if (diffSec >= 0 && diffSec < 45) return 'Just now'
      if (diffMin >= 0 && diffMin < 60) return `${diffMin}m ago`

      const sameDay = date.toDateString() === now.toDateString()
      if (sameDay) {
        if (diffHr < 6) return `${diffHr}h ago`
        return this.formatClock(date)
      }

      const yesterday = new Date(now)
      yesterday.setDate(now.getDate() - 1)
      if (date.toDateString() === yesterday.toDateString()) {
        return `Yesterday, ${this.formatClock(date)}`
      }

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${date.getDate()} ${months[date.getMonth()]}, ${this.formatClock(date)}`
    },
    formatClock (date) {
      let hour = date.getHours()
      const meridiem = hour >= 12 ? 'PM' : 'AM'
      hour = hour % 12 || 12
      const minute = String(date.getMinutes()).padStart(2, '0')
      return `${hour}:${minute} ${meridiem}`
    },
    statusClass (status) {
      const key = String(status || '').toLowerCase()
      if (key.includes('not') && key.includes('attend')) return 'call-qv__row-status--gray'
      if (key.includes('not_interested') || key === 'closed') return 'call-qv__row-status--red'
      if (key.includes('follow') || key.includes('again')) return 'call-qv__row-status--amber'
      if (key.includes('order') || key.includes('sold') || key.includes('placed')) return 'call-qv__row-status--green'
      return 'call-qv__row-status--blue'
    },
  },
}
</script>

<style scoped>
.call-qv__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(2px);
  z-index: 1040;
}

.call-qv {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  max-width: 100vw;
  background: #ffffff;
  border-left: 1px solid #e5e9f2;
  box-shadow: -16px 0 32px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  z-index: 1050;
}

/* ── Header ── */
.call-qv__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.1rem 0.9rem;
  border-bottom: 1px solid #e5e9f2;
  background: #fbfcfe;
}

.call-qv__header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.call-qv__title-block {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  min-width: 0;
}

.call-qv__accent {
  margin-top: 4px;
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  flex: 0 0 auto;
}

.call-qv__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: 0.01em;
}

.call-qv__subtitle {
  margin: 2px 0 0;
  font-size: 0.74rem;
  color: #6b7280;
  line-height: 1.3;
}

.call-qv__header-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex: 0 0 auto;
}

.call-qv__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.call-qv__icon-btn:hover:not(:disabled) {
  background: #f3f6fb;
  border-color: #c7cfdb;
  color: #1f2a44;
}

.call-qv__icon-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.call-qv__icon-btn.is-loading svg {
  animation: call-qv-spin 0.9s linear infinite;
}

@keyframes call-qv-spin {
  to { transform: rotate(360deg); }
}

/* ── Body ── */
.call-qv__body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.9rem 1.1rem 1.1rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 23, 42, 0.18) transparent;
}

.call-qv__body::-webkit-scrollbar {
  width: 6px;
}

.call-qv__body::-webkit-scrollbar-thumb {
  background-color: rgba(15, 23, 42, 0.16);
  border-radius: 999px;
}

.call-qv__body::-webkit-scrollbar-thumb:hover {
  background-color: rgba(15, 23, 42, 0.28);
}

/* ── Sections ── */
.call-qv__section {
  margin-bottom: 1.1rem;
}

.call-qv__section:last-child {
  margin-bottom: 0;
}

.call-qv__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.55rem;
  gap: 0.5rem;
}

.call-qv__section-head h3 {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #1f2a44;
}

.call-qv__hint {
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 500;
}

.call-qv__badge-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  padding: 0 6px;
  height: 18px;
  border-radius: 999px;
  background: #eef1f7;
  color: #4b5563;
  font-size: 0.66rem;
  font-weight: 700;
}

/* ── Dial ── */
.call-qv__dial {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  padding: 0.85rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.call-qv__dial-row {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
}

.call-qv__input-wrap {
  position: relative;
  flex: 1 1 auto;
}

.call-qv__input-prefix {
  position: absolute;
  top: 50%;
  left: 0.65rem;
  transform: translateY(-50%);
  color: #16a07a;
  pointer-events: none;
}

.call-qv__dial-input {
  width: 100%;
  height: 42px;
  padding: 0 2.4rem 0 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 10px;
  background: #ffffff;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #1f2a44;
  font-variant-numeric: tabular-nums;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.call-qv__dial-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.call-qv__dial-input--error,
.call-qv__dial-input--error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18);
}

.call-qv__input-clear {
  position: absolute;
  top: 50%;
  right: 0.45rem;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 0;
  background: #eef1f7;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}

.call-qv__input-clear:hover {
  background: #dde3ee;
  color: #1f2a44;
}

.call-qv__dial-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.65rem;
}

.call-qv__ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 34px;
  padding: 0 0.85rem;
  border: 1px solid #d4ece1;
  border-radius: 8px;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.call-qv__ghost-btn:hover:not(:disabled) {
  background: #f5fbf8;
  border-color: #16a07a;
}

.call-qv__ghost-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.call-qv__primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 34px;
  padding: 0 1rem;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  margin-left: auto;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.call-qv__primary-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.call-qv__primary-btn:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.call-qv__primary-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
  filter: saturate(0.6);
}

.call-qv__dial-error {
  margin: 0.55rem 0 0;
  font-size: 0.72rem;
  font-weight: 600;
  color: #b91c1c;
}

.call-qv__dial-hint {
  margin: 0.55rem 0 0;
  font-size: 0.72rem;
  color: #6b7280;
}

/* ── List ── */
.call-qv__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.call-qv__row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.7rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.12s ease, box-shadow 0.12s ease, transform 0.12s ease, background 0.12s ease;
}

.call-qv__row:hover {
  border-color: #16a07a;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.call-qv__row:active {
  transform: translateY(1px);
}

.call-qv__avatar {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.02em;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.18);
}

.call-qv__row-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.call-qv__row-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
}

.call-qv__row-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.call-qv__row-time {
  flex: 0 0 auto;
  font-size: 0.68rem;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.call-qv__row-line--meta {
  align-items: center;
}

.call-qv__row-phone {
  font-size: 0.74rem;
  color: #4b5563;
  font-variant-numeric: tabular-nums;
}

.call-qv__row-status {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  padding: 0.05rem 0.45rem;
  border-radius: 6px;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.call-qv__row-status--gray  { background: #f1f5f9; color: #475569; }
.call-qv__row-status--blue  { background: #dbeafe; color: #1e40af; }
.call-qv__row-status--amber { background: #fef3c7; color: #92400e; }
.call-qv__row-status--green { background: #dff5ea; color: #0e5b46; }
.call-qv__row-status--red   { background: #fee2e2; color: #b91c1c; }

.call-qv__row-action {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #d4ece1;
  background: #ffffff;
  color: #0e5b46;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.call-qv__row:hover:not(.call-qv__row--no-phone) .call-qv__row-action:not(:disabled) {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
}

a.call-qv__row-action,
a.call-qv__row-action:hover,
a.call-qv__row-action:focus {
  color: #0e5b46;
  text-decoration: none;
}

.call-qv__row:hover a.call-qv__row-action {
  color: #ffffff;
}

.call-qv__row-action:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.call-qv__row--no-phone {
  cursor: default;
  opacity: 0.7;
}

.call-qv__row--no-phone:hover {
  border-color: #e5e9f2;
  box-shadow: none;
}

/* ── State / skeleton ── */
.call-qv__skeletons {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.call-qv__skeleton {
  height: 52px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f1f3f8 0%, #e8ecf3 50%, #f1f3f8 100%);
  background-size: 200% 100%;
  animation: call-qv-shimmer 1.4s ease-in-out infinite;
}

@keyframes call-qv-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.call-qv__state {
  padding: 1.25rem 0.5rem;
  text-align: center;
  font-size: 0.82rem;
  color: #6b7280;
  background: #fbfcfe;
  border: 1px dashed #e5e9f2;
  border-radius: 10px;
}

.call-qv__state p {
  margin: 0 0 0.3rem;
  color: #1f2a44;
  font-weight: 600;
}

.call-qv__state-hint {
  font-size: 0.72rem;
  color: #6b7280;
}

.call-qv__state--error {
  background: #fff5f5;
  border-color: #fecaca;
  color: #b91c1c;
}

.call-qv__state--error p {
  color: #b91c1c;
}

.call-qv__retry {
  padding: 0.4rem 0.85rem;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  font-size: 0.74rem;
  font-weight: 600;
  color: #1f2a44;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
}

.call-qv__retry:hover {
  background: #f3f6fb;
  border-color: #c7cfdb;
}

/* ── Footer ── */
.call-qv__footer {
  padding: 0.75rem 1.1rem;
  border-top: 1px solid #e5e9f2;
  background: #fbfcfe;
}

.call-qv__footer-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 38px;
  border: 1px solid #d4ece1;
  border-radius: 10px;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

.call-qv__footer-btn:hover {
  background: #f5fbf8;
  border-color: #16a07a;
}

.call-qv__footer-arrow {
  transition: transform 0.15s ease;
}

.call-qv__footer-btn:hover .call-qv__footer-arrow {
  transform: translateX(3px);
}

/* ── Transitions ── */
.call-qv-slide-enter-active,
.call-qv-slide-leave-active {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.call-qv-slide-enter,
.call-qv-slide-leave-to {
  transform: translateX(100%);
}

.call-qv-fade-enter-active,
.call-qv-fade-leave-active {
  transition: opacity 0.18s ease;
}

.call-qv-fade-enter,
.call-qv-fade-leave-to {
  opacity: 0;
}

@media (max-width: 575px) {
  .call-qv {
    width: 100%;
    border-left: 0;
  }
}
</style>
